/**
 * 统计报表相关API服务
 */
import api from './api';
import {
  ApiResponse,
  ProjectComprehensiveStats,
  ProjectComparisonData,
  TimeRangeAnalysisData,
  CostSummaryData,
  ReportQueryParams,
  ProjectComparisonRequest,
} from '../types';

// 概览统计数据类型
export interface OverviewStats {
  totalProjects: number;
  activeProjects: number;
  totalWorkHours: number;
  monthlyWorkHours: number;
  totalEquipmentUsage: number;
  totalMaterialUsage: number;
  totalCost: number;
  monthlyCost: number;
}

// 趋势数据类型
export interface TrendData {
  costTrend: Array<{ date: string; cost: number }>;
  workHoursTrend: Array<{ date: string; hours: number }>;
}

// 项目分布数据类型
export interface DistributionData {
  projectStatus: Array<{ status: string; count: number }>;
}

// Dashboard总览数据类型
export interface DashboardOverviewData {
  overview: {
    totalProjects: number;
    activeProjects: number;
    totalCost: number;
    totalWorkHours: number;
    totalBudget: number;
    totalBudgetUsageRate: number | null;
    overBudgetProjects: number;
    noBudgetProjects: number;
    staleProjects: number;
    overdueProjects: number;
  };
  projects: Array<{
    id: string;
    name: string;
    status: string;
    budget: number;
    actualCost: number;
    budgetUsageRate: number | null;
    progress: number;
    startDate: string;
    endDate?: string;
    teamSize: number;
    daysRemaining: number | null;
    scheduleStatusLabel: string;
    recentActivity: string;
    subcontractCost?: number;
    coordinationCost?: number;
    revenueTotal?: number;
    budgetVersion?: number;
    riskTags: string[];
    riskReasons: string[];
    riskScore: number;
    primaryRisk: string | null;
    updatedAt?: string;
  }>;
}

export interface ProjectOperatingInsights {
  dashboard: {
    operatingSummary: {
      contractRevenue: number;
      budgetTotal: number;
      actualCostTotal: number;
      estimatedProfit: number;
      budgetExecutionRate: number;
      budgetRemaining: number;
    };
    costBreakdown: {
      laborCost: number;
      materialCost: number;
      equipmentCost: number;
      subcontractLegacyCost: number;
      coordinationCost: number;
    };
    budgetStatus: {
      level: 'healthy' | 'warning' | 'danger' | 'over';
      message: string;
    };
    caliberNotes: string[];
  };
  workItemRanking: Array<{
    _id: string;
    itemNo?: string;
    name: string;
    sourceType?: string;
    dynamicTargetCost: number;
    actualCost: number;
    outputValue: number;
    grossMarginGap: number;
    outputDeviation: number;
    riskScore: number;
    warningLevel: 'high' | 'medium' | 'low';
    rectificationCost?: number;
    linkedSubcontractCost?: number;
    linkedCoordinationCost?: number;
  }>;
  sourceAnalysis: Array<{
    sourceType: string;
    workItemCount: number;
    dynamicTargetCost: number;
    actualCost: number;
    outputValue: number;
    grossMarginGap: number;
    outputDeviation: number;
  }>;
  riskAlerts: Array<{
    workItemId: string;
    itemNo?: string;
    name: string;
    warningLevel: 'high' | 'medium' | 'low';
    riskScore: number;
    reasons: string[];
  }>;
  businessChain: {
    approvedAdditionCount: number;
    settledRectificationCount: number;
    highRiskCount: number;
  };
}

export class StatisticsService {
  /**
   * 获取Dashboard总览数据（聚合接口）
   * 一次性返回所有项目的统计数据，避免N+1请求问题
   */
  static async getDashboardOverview(): Promise<DashboardOverviewData> {
    try {
      const response = await api.get('/statistics/dashboard-overview');
      return (response as any).data || {
        overview: {
          totalProjects: 0,
          activeProjects: 0,
          totalCost: 0,
          totalWorkHours: 0
        },
        projects: []
      };
    } catch (error) {
      console.error('获取Dashboard总览数据失败:', error);
      return {
        overview: {
          totalProjects: 0,
          activeProjects: 0,
          totalCost: 0,
          totalWorkHours: 0
        },
        projects: []
      };
    }
  }

  /**
   * 获取概览统计数据
   */
  static async getOverviewStats(params: { startDate?: string; endDate?: string } = {}): Promise<OverviewStats> {
    try {
      const response = await api.get('/statistics/overview', { params });
      return (response as any).data || {
        totalProjects: 0,
        activeProjects: 0,
        totalWorkHours: 0,
        monthlyWorkHours: 0,
        totalEquipmentUsage: 0,
        totalMaterialUsage: 0,
        totalCost: 0,
        monthlyCost: 0
      };
    } catch (error) {
      console.error('获取概览统计失败:', error);
      return {
        totalProjects: 0,
        activeProjects: 0,
        totalWorkHours: 0,
        monthlyWorkHours: 0,
        totalEquipmentUsage: 0,
        totalMaterialUsage: 0,
        totalCost: 0,
        monthlyCost: 0
      };
    }
  }

  /**
   * 获取趋势数据
   */
  static async getTrendData(params: { startDate: string; endDate: string }): Promise<TrendData> {
    try {
      const response = await api.get('/statistics/trend', { params });
      return (response as any).data || { costTrend: [], workHoursTrend: [] };
    } catch (error) {
      console.error('获取趋势数据失败:', error);
      return { costTrend: [], workHoursTrend: [] };
    }
  }

  /**
   * 获取项目分布数据
   */
  static async getProjectDistribution(): Promise<DistributionData> {
    try {
      const response = await api.get('/statistics/distribution');
      return (response as any).data || { projectStatus: [] };
    } catch (error) {
      console.error('获取项目分布数据失败:', error);
      return { projectStatus: [] };
    }
  }
  /**
   * 获取项目综合统计报表
   */
  static async getProjectComprehensiveStats(
    projectId: string,
    params: Omit<ReportQueryParams, 'projectId'> = {}
  ): Promise<ApiResponse<ProjectComprehensiveStats>> {
    return api.get(`/statistics/project/${projectId}/comprehensive`, { params }) as Promise<ApiResponse<ProjectComprehensiveStats>>;
  }

  /**
   * 获取项目经营分析与预警
   */
  static async getProjectOperatingInsights(
    projectId: string,
    params: Omit<ReportQueryParams, 'projectId'> = {}
  ): Promise<ApiResponse<ProjectOperatingInsights>> {
    return api.get(`/reports/project-cost-linkage/${projectId}/insights`, { params }) as Promise<ApiResponse<ProjectOperatingInsights>>;
  }

  /**
   * 获取多项目对比统计
   */
  static async getProjectComparison(
    data: ProjectComparisonRequest
  ): Promise<ApiResponse<ProjectComparisonData>> {
    return api.post('/statistics/projects/comparison', data) as Promise<ApiResponse<ProjectComparisonData>>;
  }

  /**
   * 获取时间段分析数据
   */
  static async getTimeRangeAnalysis(
    params: ReportQueryParams
  ): Promise<ApiResponse<TimeRangeAnalysisData>> {
    return api.get('/statistics/time-range-analysis', { params }) as Promise<ApiResponse<TimeRangeAnalysisData>>;
  }

  /**
   * 获取成本汇总统计
   */
  static async getCostSummary(
    params: ReportQueryParams = {}
  ): Promise<ApiResponse<CostSummaryData>> {
    const queryParams = {
      ...params,
      projectIds: params.projectIds?.join(','),
    };
    return api.get('/statistics/cost-summary', { params: queryParams }) as Promise<ApiResponse<CostSummaryData>>;
  }

  /**
   * 导出项目综合报表 (Excel)
   */
  static async exportProjectExcel(
    projectId: string,
    params: Omit<ReportQueryParams, 'projectId'> = {}
  ): Promise<Blob> {
    const response = await api.get(`/statistics/export/project/${projectId}/excel`, {
      params,
      responseType: 'blob',
    });
    return (response as unknown) as Blob;
  }

  /**
   * 导出项目综合报表 (PDF)
   */
  static async exportProjectPdf(
    projectId: string,
    params: Omit<ReportQueryParams, 'projectId'> = {}
  ): Promise<Blob> {
    const response = await api.get(`/statistics/export/project/${projectId}/pdf`, {
      params,
      responseType: 'blob',
    });
    return (response as unknown) as Blob;
  }

  /**
   * 导出多项目对比报表 (Excel)
   */
  static async exportProjectComparisonExcel(
    data: ProjectComparisonRequest
  ): Promise<Blob> {
    const response = await api.post('/statistics/export/projects/comparison/excel', data, {
      responseType: 'blob',
    });
    return (response as unknown) as Blob;
  }

  /**
   * 导出时间段分析报表 (Excel)
   */
  static async exportTimeRangeAnalysisExcel(
    params: ReportQueryParams
  ): Promise<Blob> {
    const response = await api.get('/statistics/export/time-range-analysis/excel', {
      params,
      responseType: 'blob',
    });
    return (response as unknown) as Blob;
  }

  /**
   * 导出成本汇总报表 (Excel)
   */
  static async exportCostSummaryExcel(
    params: ReportQueryParams = {}
  ): Promise<Blob> {
    const queryParams = {
      ...params,
      projectIds: params.projectIds?.join(','),
    };
    const response = await api.get('/statistics/export/cost-summary/excel', {
      params: queryParams,
      responseType: 'blob',
    });
    return (response as unknown) as Blob;
  }

  /**
   * 下载文件的通用方法
   */
  static downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}